import { Component, OnInit, OnChanges, SimpleChanges, Input, Output, EventEmitter, ElementRef, HostListener } from '@angular/core';
import { Router } from '@angular/router';
import { WebServiceService } from '../../shared/services/web-service.service';
import { EesDesService } from '../../shared/services/ees-des.service';
import * as pluginDataLabels from 'chartjs-plugin-datalabels';

@Component({
  selector: 'app-dynamic-project-chart',
  templateUrl: './dynamic-project-chart.component.html',
  styleUrls: ['./dynamic-project-chart.component.scss']
})
export class DynamicProjectChartComponent implements OnInit, OnChanges {
  // @Input() dataTosend: any;
  // Pie
  @Input() labels: string[];
  @Input() data: number[];
  @Input() pieChartType?: string = 'pie';
  @Input() colors?: any = ['#5fb2c6', '#53a759', '#e88b44', '#e23f3d']
  @Input() lineChartLegend?:boolean=true;
  @Input() title:any;
  @Input() chartData?: any; // Additional data for navigation (e.g., invoice ageing items)
  // Keep selected label visible after chart refresh (parent passes it back)
  @Input() selectedEmployeeName: string = '';
  @Output() amcEmployeeSelected = new EventEmitter<any>();
  pieChartColors: any;
  public pieChartPlugins = [pluginDataLabels];
  amcEmployees: Array<{ id: any; employee_name: string }> = [];
  filteredAmcEmployees: Array<{ id: any; employee_name: string }> = [];
  private amcEmployeesMaster: Array<{ id: any; employee_name: string }> = [];
  amcEmployeesLoading: boolean = false;
  selectedEmployeeId: any = '';
  selectedEmployeeLabel: string = '';
  employeeSearchTerm: string = '';
  isEmployeeDropdownOpen: boolean = false;
  private employeeSearchDebounceRef: any = null;
  private lastEmployeesQuery: string = '';

  public options: any = {
    legend: {
      position: 'right',
      orient: 'vertical',
    },
    plugins: {
      datalabels: {
        display:false
      },
    }

  }

  constructor(
    private router: Router,
    public webService: WebServiceService,
    private eesdesService: EesDesService,
    private elRef: ElementRef
  ) { }

  ngOnInit() {
    this.pieChartColors = [{ backgroundColor: this.colors }];
    if (this.selectedEmployeeName) {
      this.selectedEmployeeLabel = String(this.selectedEmployeeName);
    }
    this.maybeInitAmcEmployees();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedEmployeeName'] && changes['selectedEmployeeName'].currentValue !== undefined) {
      const v = changes['selectedEmployeeName'].currentValue;
      this.selectedEmployeeLabel = v ? String(v) : '';
    }
    if (changes['chartData']) {
      this.maybeInitAmcEmployees();
    }
  }

  private maybeInitAmcEmployees() {
    if (!this.chartData || this.chartData.key !== 'amc_overdue_ageing_all') {
      return;
    }
    if (this.amcEmployeesLoading || this.amcEmployees.length > 0) {
      return;
    }
    this.fetchAmcEmployees();
  }

  private fetchAmcEmployees(query: string = '') {
    this.amcEmployeesLoading = true;
    this.lastEmployeesQuery = (query || '').trim();
    const payload: any = {};
    if (this.lastEmployeesQuery.length >= 3) {
      // Server-side search (API may accept any one of these keys)
      payload.search = this.lastEmployeesQuery;
      payload.keyword = this.lastEmployeesQuery;
      payload.employee_name = this.lastEmployeesQuery;
    }

    this.webService.commonPostMethod('Amc_dashboard/ajax_employees', payload)
      .subscribe(
        (res: any) => {
          let list = res && res.data && Array.isArray(res.data.employees) ? res.data.employees : [];
          // Only set master list when loading full list (non-search)
          if (!this.lastEmployeesQuery) {
            this.amcEmployeesMaster = list;
            this.amcEmployees = list;
            this.filteredAmcEmployees = list;
          } else {
            // Keep master list as-is; show searched results in dropdown
            this.filteredAmcEmployees = list;
          }
          this.amcEmployeesLoading = false;
        },
        (_err: any) => {
          this.amcEmployees = [];
          this.filteredAmcEmployees = [];
          this.amcEmployeesMaster = [];
          this.amcEmployeesLoading = false;
        }
      );
  }

  onEmployeeSearch(term: string) {
    this.employeeSearchTerm = term || '';
    const q = this.employeeSearchTerm.trim().toLowerCase();
    const raw = this.employeeSearchTerm.trim();

    if (this.employeeSearchDebounceRef) {
      clearTimeout(this.employeeSearchDebounceRef);
      this.employeeSearchDebounceRef = null;
    }

    if (!raw) {
      this.filteredAmcEmployees = this.amcEmployeesMaster.length ? this.amcEmployeesMaster : this.amcEmployees;
      return;
    }

    // Call API only after 3 letters
    if (raw.length >= 3) {
      this.employeeSearchDebounceRef = setTimeout(() => {
        this.fetchAmcEmployees(raw);
      }, 300);
      return;
    }

    // For 1-2 letters, filter locally from master list
    const src = this.amcEmployeesMaster.length ? this.amcEmployeesMaster : this.amcEmployees;
    this.filteredAmcEmployees = src.filter((e: any) =>
      (e && e.employee_name ? String(e.employee_name) : '').toLowerCase().includes(q)
    );
  }

  toggleEmployeeDropdown() {
    this.isEmployeeDropdownOpen = !this.isEmployeeDropdownOpen;
    if (this.isEmployeeDropdownOpen) {
      this.onEmployeeSearch(this.employeeSearchTerm);
    }
  }

  getSelectedEmployeeLabel(): string {
    return this.selectedEmployeeLabel ? this.selectedEmployeeLabel : 'Search Employee';
  }

  selectEmployee(employeeId: any, employeeName: string) {
    this.selectedEmployeeId = employeeId;
    this.selectedEmployeeLabel = employeeName ? String(employeeName).trim() : '';
    // Don't persist: dashboard should send empty on refresh
    this.amcEmployeeSelected.emit({ id: employeeId, name: this.selectedEmployeeLabel });
    this.isEmployeeDropdownOpen = false;
    this.employeeSearchTerm = '';
    this.onEmployeeSearch('');
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (!this.isEmployeeDropdownOpen) {
      return;
    }
    const clickedInside = this.elRef && this.elRef.nativeElement && this.elRef.nativeElement.contains(event.target);
    if (!clickedInside) {
      this.isEmployeeDropdownOpen = false;
    }
  }

  public chartClicked(e: any): void {
    if (e.active.length > 0) {
      const chart = e.active[0]._chart;
      const activePoints = chart.getElementAtEvent(e.event);
      if (activePoints.length > 0) {
        // get the internal index of slice in pie chart
        const clickedElementIndex = activePoints[0]._index;
        const label = chart.data.labels[clickedElementIndex];
        // get value by index
        const value = chart.data.datasets[0].data[clickedElementIndex];


        this.navigateUrl(label,value);
      }
    }
  }

  chartHovered(e: any): void {
  }

  navigateUrl(key,value) {
    // if (count <= 0) {
    //   this.toasterService.pop("info", "Info", title + " = " + count);
    //   return false;
    // }


    let url = "";
    let dashboardAction = ""

      let project='';
    switch (key) {
      case "My Project"+ " ("+value+")":
        url = "/my-project-report";
        project='reports'
        dashboardAction = ""
        break;

      case "Low Priority"+ " ("+value+")":
        url = "/my-task-master";
        project='project_management'
        dashboardAction = "low_priority_my_task"
        break;
        case "High Priority"+ " ("+value+")":
          url = "/my-task-master";
          project='project_management'
          dashboardAction = "high_priority_my_task"
          break;
          case "Medium Priority"+ " ("+value+")":
            url = "/my-task-master";
            project='project_management'
            dashboardAction = "medium_priority_my_task"
            break;

            case "Today's Low Priority Task"+ " ("+value+")":
              url = "/my-task-master";
              project='project_management'
              dashboardAction = ""
              break;
              case "Today's Low Priority Task"+ " ("+value+")":
                url = "/my-task-master";
                project='project_management'
                dashboardAction = ""
                break;
                case "Today's High Priority Task"+ " ("+value+")":
                  url = "/my-task-master";
                  project='project_management'
                  dashboardAction = ""
                  break;

                  case "Today's Medium Priority Task"+ " ("+value+")":
                    url = "/my-task-master";
                    project='project_management'
                    dashboardAction = ""
                    break;

                  case "All Project"+ " ("+value+")":
                    url = "/project-master";
                    project='project_management'
                    dashboardAction = ""
                    break;


      default:
        if (this.chartData && this.chartData.key === 'invoice_ageing' && this.chartData.items) {
          let clickedBucket = this.chartData.items.find((item: any) => item.label === key);
          if (clickedBucket) {
            let filterData = {
              from_date: clickedBucket.from_date,
              to_date: clickedBucket.to_date,
              ageing_from_date: clickedBucket.from_date,
              ageing_to_date: clickedBucket.to_date,
              days_range: clickedBucket.days_range,
              bucket: clickedBucket.bucket,
              count: clickedBucket.count,
              value: clickedBucket.value
            };
            localStorage.setItem("invoice_ageing_filter", this.eesdesService.set(JSON.stringify(filterData)));
            localStorage.setItem("dashboard_navigation_source", "invoice_ageing");
            localStorage.setItem("dashboard_navigation_bucket", clickedBucket.bucket);
            url = "/mailed-invoice";
            project = '';
          }
        }
        // AMC overdue ageing buckets -> open My AMC Task page (default overdue + scope my + clicked ageing_bucket)
        if (this.chartData && this.chartData.key === 'amc_overdue_ageing' && this.chartData.items) {
          let clickedBucket = this.chartData.items.find((item: any) => (item.labelText ? item.labelText : item.label) === key);
          if (clickedBucket) {
            const ageingBucket = clickedBucket.bucket ? String(clickedBucket.bucket) : '';
            const dashboardCardType = 'my_overdue';
            const scopeVal = 'my';
            let filterData = {
              bucket: clickedBucket.bucket,
              ageing_bucket: ageingBucket,
              dashboard_card_type: dashboardCardType,
              scope: scopeVal,
              label: clickedBucket.label,
              count: clickedBucket.count
            };
            // Store filters only in localStorage (no query string)
            localStorage.setItem("dashboard_card_type", dashboardCardType);
            localStorage.setItem("scope", scopeVal);
            localStorage.setItem("ageing_bucket", ageingBucket);
            localStorage.setItem("amc_overdue_ageing_filter", this.eesdesService.set(JSON.stringify(filterData)));
            localStorage.setItem("dashboard_navigation_source", "amc_overdue_ageing");
            localStorage.setItem("dashboard_navigation_bucket", ageingBucket);
            // localhost: open DevTools Console to verify values before redirect
            url = "/my-amc-task";
            project = 'master';
          }
        }

        // AMC overdue ageing buckets (ALL) -> All AMC Task page: filters only in localStorage (no query string)
        if (this.chartData && this.chartData.key === 'amc_overdue_ageing_all' && this.chartData.items) {
          let clickedBucket = this.chartData.items.find((item: any) => (item.labelText ? item.labelText : item.label) === key);
          if (clickedBucket) {
            const ageingBucket = clickedBucket.bucket ? String(clickedBucket.bucket) : '';
            // Employee selected in dropdown (best source). Fallback to chartData if already set by parent.
            const employeeId = this.selectedEmployeeId
              ? String(this.selectedEmployeeId)
              : (this.chartData.employee_id ? String(this.chartData.employee_id) : '');
            const dashboardCardType = 'all_overdue';
            const scopeVal = 'all';
            let filterData = {
              bucket: clickedBucket.bucket,
              ageing_bucket: ageingBucket,
              dashboard_card_type: dashboardCardType,
              scope: scopeVal,
              label: clickedBucket.label,
              count: clickedBucket.count,
              employee_id: employeeId
            };
            localStorage.setItem('dashboard_card_type', dashboardCardType);
            localStorage.setItem('scope', scopeVal);
            localStorage.setItem('ageing_bucket', ageingBucket);
            localStorage.setItem('employee_id', employeeId);
            localStorage.setItem('amc_overdue_ageing_all_filter', this.eesdesService.set(JSON.stringify(filterData)));
            localStorage.setItem('dashboard_navigation_source', 'amc_overdue_ageing_all');
            localStorage.setItem('dashboard_navigation_bucket', ageingBucket);
            // localhost: open DevTools Console to verify values before redirect
            url = '/all-amc-tasks';
            project = 'master';
          }
        }
        if (!url) {
          url = "";
        }
    }
    localStorage.setItem("dashboard_action_pm", dashboardAction);
    if(url!=''&&url!=undefined)
    {
      let redirectUrl = '/#';
      let origin = window.location.origin;
      if (project == 'master') {
        redirectUrl = origin + '/master' + redirectUrl + url;
      } else if (project == 'reports') {
        redirectUrl = origin + '/report' + redirectUrl + url;
      } else if (project == 'mapping') {
        redirectUrl = origin + '/mapping' + redirectUrl + url;
      } else if (project == 'finance') {
        redirectUrl = origin + '/finance' + redirectUrl + url;
      }
      else if (project == 'project_management') {
        redirectUrl = origin + '/project_management' + redirectUrl + url;
      }

      else {
        if (url == '#' || !url) return;
        this.webService.redirectURL(url);
        return;
      }
      window.location.href = redirectUrl;
    }
  }



}



// WEBPACK FOOTER //
// /codebuild/output/src1639192363/src/src/app/common/dynamic-project-chart/dynamic-project-chart.component.ts